/* eslint-disable max-classes-per-file */
import { useNavigate, useLocation } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { MessageContentType, MessageProps, MessageState } from './messageTypes';
import { dismissMessage, setMessage } from './messageActions';
import { useToast } from 'framework/ui/Toast';
import getMessageContent from './getMessageContent';

const useMessageService = (messageContext?: string) => {
  const dispatch = useDispatch();
  const messageContainer = useSelector((state: { message: MessageState[] }) => state.message, shallowEqual);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();

  const addMessage = (messageObject: MessageProps) => {
    if (messageObject.status === 'success') {
      toast.success(getMessageContent(messageObject.content));
      return;
    }

    dispatch(setMessage(messageObject, pathname, messageContext));
  };

  const addMessageAndRedirect = (messageObject: MessageProps, redirectUrl: string) => {
    if (messageObject.status === 'success') {
      toast.success(getMessageContent(messageObject.content));
      navigate(redirectUrl);
      return;
    }

    navigate('/');
    navigate(redirectUrl);
    dispatch(setMessage(messageObject, redirectUrl));
  };

  const cleanMessage = (messageObject?: MessageProps) => {
    dispatch(dismissMessage(messageObject, pathname, messageContext));
  };

  useEffect(() => {
    if (messageContainer && messageContainer.some(m => m.pathname !== pathname)) {
      cleanMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return {
    addMessage,
    addMessageAndRedirect,
    cleanMessage,
  };
};

interface Message {
  status: string;
  content: MessageContentType;
}

export class ErrorMessage implements Message {
  status: string;

  content: MessageContentType;

  constructor(content: MessageContentType) {
    this.status = 'error';
    this.content = content;
  }
}

export class SuccessMessage implements Message {
  status: string;

  content: MessageContentType;

  constructor(content: MessageContentType) {
    this.status = 'success';
    this.content = content;
  }
}

export class WarningMessage implements Message {
  status: string;

  content: MessageContentType;

  closeable: boolean;

  constructor(content: MessageContentType) {
    this.status = 'warning';
    this.content = content;
    this.closeable = false;
  }
}

export class InfoMessage implements Message {
  status: string;

  content: MessageContentType;

  constructor(content: MessageContentType) {
    this.status = 'info';
    this.content = content;
  }
}

export default useMessageService;
